import React from 'react';
import { ShoppingBag, ExternalLink, Truck, ShieldCheck, Sparkles } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const THREADLESS_URL = 'https://rhodiumrecords.threadless.com';

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Premium Quality',
    desc: 'Every drop is printed on premium garments and inspected before it ships.'
  },
  {
    icon: Truck,
    title: 'Worldwide Shipping',
    desc: 'Threadless prints and ships from multiple regions for fast global delivery.'
  },
  {
    icon: ShieldCheck,
    title: 'Trusted Fulfillment',
    desc: 'Orders, payments, and returns are securely handled by Threadless.'
  }
];

const StoreHero = () => (
  <div className="mb-16 text-center max-w-3xl mx-auto">
    <span className="inline-block px-4 py-1.5 mb-6 text-xs uppercase tracking-[0.3em] text-silver-400 border border-silver-400/30 rounded-full">
      Official Merch
    </span>
    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-5">
      <span className="text-white">Wear the </span>
      <span className="logo-text">Element</span>
    </h1>
    <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
      Limited edition Rhodium Records apparel and accessories — designed in-house, fulfilled by{' '}
      <span className="text-silver-400 font-semibold">Threadless</span>.
    </p>
    <a
      href={THREADLESS_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="store-threadless-cta"
      className="inline-flex items-center gap-3 px-10 py-5 bg-silver-400 hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-[0.2em] rounded-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-silver-400/20"
    >
      <ShoppingBag size={20} />
      Shop the Collection
      <ExternalLink size={16} />
    </a>
  </div>
);

const ShowcaseTile = () => (
  <div className="relative max-w-5xl mx-auto mb-20">
    <div className="bg-gradient-to-br from-silver-900/40 to-silver-900/10 border-2 border-silver-400/30 rounded-3xl overflow-hidden">
      <a
        href={THREADLESS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block group relative"
        data-testid="store-threadless-showcase"
      >
        <div className="aspect-[16/9] md:aspect-[21/9] relative bg-black overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1521417531039-75e91486cc40?w=1600&q=80"
            alt="Rhodium Records merch on Threadless"
            className="absolute inset-0 w-full h-full object-cover opacity-60 transition-all duration-700 group-hover:opacity-80 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span className="text-silver-400 text-xs uppercase tracking-[0.3em] mb-3">
              rhodiumrecords.threadless.com
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Browse the Full Shop</h2>
            <p className="text-white/70 max-w-xl mb-6">
              Tees, hoodies, posters, accessories — explore every drop on our Threadless storefront.
            </p>
            <span className="inline-flex items-center gap-2 px-6 py-3 bg-silver-400 group-hover:bg-silver-300 text-black font-bold text-sm uppercase tracking-wider rounded-lg transition-colors">
              Open Threadless
              <ExternalLink size={16} />
            </span>
          </div>
        </div>
      </a>
    </div>
  </div>
);

const FeatureGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20">
    {FEATURES.map(({ icon: Icon, title, desc }) => (
      <div
        key={title}
        className="bg-silver-900/10 border border-silver-900/30 rounded-xl p-6 hover:border-silver-400/40 transition-all duration-300"
      >
        <div className="w-12 h-12 rounded-full bg-silver-400/10 flex items-center justify-center mb-4">
          <Icon size={22} className="text-silver-400" />
        </div>
        <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
        <p className="text-white/60 text-sm leading-relaxed">{desc}</p>
      </div>
    ))}
  </div>
);

const BottomCTA = () => (
  <div className="text-center">
    <p className="text-white/50 text-sm mb-4">
      All orders, payments, sizing and shipping are handled directly by Threadless.
    </p>
    <a
      href={THREADLESS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 text-silver-400 hover:text-silver-300 font-medium underline underline-offset-4"
    >
      Visit rhodiumrecords.threadless.com
      <ExternalLink size={14} />
    </a>
  </div>
);

const Store = () => {
  usePageMeta(
    'Official Merch Store | Rhodium Records',
    'Limited edition Rhodium Records apparel and accessories — tees, hoodies, posters and more, fulfilled by Threadless.'
  );
  return (
  <div className="min-h-screen bg-black pt-24 pb-32">
    <div className="container mx-auto px-6">
      <StoreHero />
      <ShowcaseTile />
      <FeatureGrid />
      <BottomCTA />
    </div>
  </div>
  );
};

export default Store;
